import { SceneContext, sceneState, type SceneState } from '@/lib/threeScene/SceneContext';
import { renderingState, type RenderingState } from '@/lib/threeScene/renderingState';
import { type StandardNode } from '@/lib/threeScene/updateNode';
import {
    Matrix4,
    MeshBasicMaterial,
    type Object3D,
    type OrthographicCamera,
    Quaternion,
    SphereGeometry,
    Vector2,
    Vector3,
} from 'three';
import { imagePlaneNode, type ImagePlaneNode } from '@/lib/threeScene/nodes/imagePlane';
import { meshNode, type MeshNode } from '@/lib/threeScene/nodes/mesh';
import { prototypeLandmarks } from '@/views/prototype/prototypeLandmarks';
import { createImageViewerCameraState } from '@/lib/threeScene/cameras/imageViewerCamera';
import { prototypeScene } from '@/views/prototype/prototypeScene';
import {
    XRAY_CAMERA_FRUSTUM_HEIGHT,
    XRAY_CAMERA_TARGET_DISTANCE,
} from '@/views/dicoms/xrays/xraySceneState';
import { vector3 } from '@/domain/geometry/vector3';
import { createPlaneProjection } from '@/lib/threeScene/interaction/createPlaneProjection';
import {
    createPointerDragController,
    type ProjectedPointer,
} from '@/lib/threeScene/interaction/createPointerDragController';
import type { StopHandle } from '@/util';

export type PrototypeXraySceneNode = StandardNode;

export type PrototypeXraySceneState = SceneState<PrototypeXraySceneNode> & {
    rendering: RenderingState;
    nodes: {
        imagePlane: ImagePlaneNode;
        landmarks: MeshNode[];
    };
};

export function prototypeXraySceneState(): PrototypeXraySceneState {
    return {
        ...sceneState(),
        rendering: renderingState({
            camera: createImageViewerCameraState({
                zoom: 1,
                position: vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE),
                up: vector3(0, 1, 0),
                height: XRAY_CAMERA_FRUSTUM_HEIGHT,
            }),
        }),
        nodes: {
            imagePlane: imagePlaneNode('prototype-xray', {visible: false}),
            landmarks: prototypeLandmarks.landmarks.map((landmark, i) =>
                meshNode(`${i}`, {
                    name: landmark.landmark_name,
                    geometry: new SphereGeometry(0.01, 32, 32),
                    material: new MeshBasicMaterial({color: '#ff0000'}),
                    transform: new Matrix4().setPosition(landmark.x, landmark.y, 0),
                })
            ),
        },
    };
}

export class PrototypeXraySceneContext extends SceneContext<PrototypeXraySceneNode, PrototypeXraySceneState> {
}

export function prototypeXraySceneContext(state: PrototypeXraySceneState): PrototypeXraySceneContext {
    const scene = prototypeScene();
    return new PrototypeXraySceneContext(scene, state);
}

type LandmarkHit = {
    nodeIndex: number;
    /** Offset from the node's position to the pointerdown world point. */
    offset: Vector2;
    /** Z component of the node's transform at drag start; preserved through the drag. */
    originalZ: number;
};

export function setupLandmarkRaycasting(
    sceneContext: PrototypeXraySceneContext,
    camera: OrthographicCamera,
    canvas: HTMLCanvasElement,
    onPositionUpdate?: (index: number, position: { x: number; y: number; z: number }) => void,
): StopHandle {
    const projection = createPlaneProjection(canvas, camera);

    function getLandmarkObjects(): Object3D[] {
        const objects: Object3D[] = [];
        for (const node of sceneContext.state.nodes.landmarks) {
            if (!node.name) continue;
            const obj = sceneContext.scene.getObjectByName(node.name);
            if (obj) objects.push(obj);
        }
        return objects;
    }

    const pointerProjection = (event: PointerEvent): ProjectedPointer<LandmarkHit> => {
        const world = projection.project(event);
        const intersects = projection.raycaster.intersectObjects(getLandmarkObjects());
        if (intersects.length === 0) return { hit: null, world };

        const hitObject = intersects[0].object;
        const nodes = sceneContext.state.nodes.landmarks;
        const nodeIndex = nodes.findIndex(
            (n) => n.name !== null && sceneContext.scene.getObjectByName(n.name) === hitObject,
        );
        if (nodeIndex === -1) return { hit: null, world };

        const node = nodes[nodeIndex];
        const pos = new Vector3();
        node.transform.decompose(pos, new Quaternion(), new Vector3());

        return {
            hit: {
                nodeIndex,
                offset: new Vector2(world.x - pos.x, world.y - pos.y),
                originalZ: pos.z,
            },
            world,
        };
    };

    return createPointerDragController<LandmarkHit>({
        canvas,
        pointerProjection,
        onDrag: (hit, world) => {
            const node = sceneContext.state.nodes.landmarks[hit.nodeIndex];
            const newX = world.x - hit.offset.x;
            const newY = world.y - hit.offset.y;
            node.transform.setPosition(newX, newY, hit.originalZ);
            onPositionUpdate?.(hit.nodeIndex, { x: newX, y: newY, z: hit.originalZ });
        },
        setControlsEnabled: (enabled) => {
            sceneContext.state.rendering.camera.enableControls = enabled;
            canvas.style.cursor = enabled ? 'default' : 'grabbing';
        },
    });
}
